import { Component, Input, OnInit, ViewChildren, QueryList, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Form, FormField, SignatureOptions } from '../../../../vendors/directus/interfaces/form.interface';
import { ContentService } from '../../../../services/vendor-config-service/content-provider.service';
import { Select, Store } from '@ngxs/store';
import { GlobalStateModel } from '../../../../store/state.model';
import { combineLatest, forkJoin, Observable, of, shareReplay, Subject, switchMap, takeUntil } from 'rxjs';
import { Location } from '../../../../interfaces/location.interface';
import { SetStaticLocations } from '../../../../store/actions/location.actions';
import { ProxyBaseURLService } from '../../../../services/proxy-base-url.service';
import { SignaturePadComponent } from '@common/components';
import { LocationService } from '../../../../services/vendor-config-service/location.service';
import { filter, map } from 'rxjs/operators';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Select((state: GlobalStateModel) => state.location.staticLocations) staticLocations$: Observable<Location[]>;
  @ViewChildren(SignaturePadComponent) signaturePads: QueryList<SignaturePadComponent>;

  @Input() forminternalid: string;

  private destroy$ = new Subject<void>();

  private fileStorage: { [key: string]: FileList | null } = {};
  private signaturePadsByFieldName: Map<string, SignaturePadComponent> = new Map();

  forms: Form[] = [];
  formFields: FormField[] = [];
  formConfig: Form;
  dynamicForm: FormGroup;
  selectedFormId: string;

  staticLocations: Location[] = [];

  // User feedback properties
  submissionSuccess = false;
  submissionError = false;
  isSubmitting = false;

  // ➊  Cache the location-service instance so we only fetch it once
  private lService$ = this.locationService.getService().pipe(shareReplay(1));

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private contentService: ContentService,
    private store: Store,
    private baseURL: ProxyBaseURLService,
    private locationService: LocationService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new SetStaticLocations());
    this.contentService.getService().subscribe(service => {
      service.getForms().subscribe(forms => {
        this.forms = forms;
        this.loadForm(this.forminternalid);
      });
    });
    combineLatest([this.staticLocations$, this.lService$])
      .pipe(
        filter(([locations]) => !!locations && !!locations.length),
        switchMap(([locations, lService]) => {
          const needsRemote = lService.providerName === 'olo' || lService.providerName === 'punchh-olo';

          /* Kick off all missing-hours requests in parallel */
          const requests = locations.map(loc => {
            if (needsRemote && (!loc.pickupHours || loc.pickupHours.length === 0)) {
              return lService.getLocation(loc.locationID);
            }
            return of(loc);
          });

          return forkJoin(requests);
        }),

        /* Optional: keep the UI predictable */
        map(locs => locs.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))),

        takeUntil(this.destroy$)
      )
      .subscribe(locs => (this.staticLocations = locs));
  }

  ngAfterViewInit(): void {
    // Map signature pads to their field names with a delay to ensure they're rendered
    setTimeout(() => {
      this.mapSignaturePadsToFields();
    }, 500);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Map signature pads to their field names
  private mapSignaturePadsToFields(): void {
    if (this.signaturePads && this.signaturePads.length > 0) {
      this.signaturePadsByFieldName.clear(); // Make sure the map is empty
      this.signaturePads.forEach(pad => {
        // Use the fieldName input property instead of the attribute
        if (pad.fieldName) {
          this.signaturePadsByFieldName.set(pad.fieldName, pad);
          console.log(`Mapped signature pad for field: ${pad.fieldName}`);
        }
      });

      // Log for debugging
      console.log('Signature pads mapped:', this.signaturePadsByFieldName.size);
    }
  }

  loadForm(formId: string): void {
    this.selectedFormId = formId;
    this.formConfig = this.forms.find(form => form.internal_id === formId);
    this.formFields = this.formConfig.form_fields;
    this.buildForm();
  }

  buildForm(): void {
    const group = {};
    this.formFields.forEach(field => {
      const validators = [];
      if (field.required) {
        validators.push(Validators.required);
      }
      // Additional validators based on field.validation can be added here

      if (field.type === 'upload') {
        group[field.name] = [null];
      } else if (field.type === 'signature') {
        group[field.name] = ['', validators]; // Store signature as base64 string
      } else if (field.type === 'hidden') {
        group[field.name] = [field.default_value, validators];
      } else {
        group[field.name] = ['', validators];
      }
    });
    this.dynamicForm = this.fb.group(group);
  }

  onSubmit(event: Event): void {
    if (this.formConfig.submission_type === 'email') {
      event.preventDefault();

      // Capture all signatures before submission
      this.captureAllSignatures();

      if (this.dynamicForm.valid) {
        this.handleEmailSubmission();
      } else {
        this.markAllAsTouched();
      }
    }
  }

  captureAllSignatures(): void {
    // Get all signature fields
    const signatureFields = this.formFields.filter(field => field.type === 'signature');

    // Loop through each signature field and capture the signature
    signatureFields.forEach(field => {
      const pad = this.signaturePadsByFieldName.get(field.name);
      if (pad && !pad.isCanvasEmpty()) {
        const signatureData = pad.toDataURL();
        if (signatureData) {
          this.dynamicForm.get(field.name).setValue(signatureData);
        }
      }
    });
  }

  handleEmailSubmission(): void {
    const formData = new FormData();

    // Append form fields to FormData
    for (const key in this.dynamicForm.value) {
      if (Object.prototype.hasOwnProperty.call(this.dynamicForm.value, key)) {
        const value = this.dynamicForm.get(key).value;

        // Use the stored files for file inputs
        if (this.fileStorage[key]) {
          const files = this.fileStorage[key];
          if (files.length > 1) {
            // For multiple files, use array notation
            for (let i = 0; i < files.length; i++) {
              formData.append(`${key}[]`, files.item(i), files.item(i).name);
            }
          } else if (files.length === 1) {
            // For single file, use the original key
            formData.append(key, files.item(0), files.item(0).name);
          }
        } else {
          formData.append(key, value);
        }
      }
    }

    this.isSubmitting = true;
    this.submissionSuccess = false;
    this.submissionError = false;
    this.baseURL.getBaseURL().subscribe(baseURL => {
      this.http.post(`${baseURL}form_submission/${this.forminternalid}`, formData).subscribe(
        response => {
          console.log('Email sent successfully:', response);
          this.submissionSuccess = true;
          this.isSubmitting = false;
          this.dynamicForm.reset();
          this.fileStorage = {}; // Clear stored files
          this.clearAllSignatures(); // Clear all signatures
        },
        error => {
          console.error('Error sending email:', error);
          this.submissionError = true;
          this.isSubmitting = false;
        }
      );
    });
  }

  markAllAsTouched(): void {
    Object.values(this.dynamicForm.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  onFileChange(event: Event, field: FormField): void {
    const input = event.target as HTMLInputElement;
    const files: FileList = input.files;

    const allowedTypes = this.getAllowedTypes(field);
    const maxSize = this.getMaxSize(field);

    // Validate files
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!allowedTypes.includes(file.type)) {
        alert(`File type not allowed: ${file.type}`);
        input.value = ''; // Reset the file input
        return;
      }
      if (file.size > maxSize) {
        alert(`File size exceeds limit: ${file.size} bytes`);
        input.value = ''; // Reset the file input
        return;
      }
    }

    // Store the FileList in the local variable
    this.fileStorage[field.name] = files;
  }

  // Signature pad methods
  getSignatureOptions(field: FormField): SignatureOptions {
    const defaultOptions: SignatureOptions = {
      width: 400,
      height: 200,
      backgroundColor: '#ffffff',
      penColor: '#000000',
      penWidth: 2,
    };

    // Merge with options from field if available
    if (field.signature_options) {
      return {
        ...defaultOptions,
        ...field.signature_options,
      };
    }

    return defaultOptions;
  }

  onSignatureBegin(): void {
    // Optional: Handle when user starts signing
  }

  onSignatureEnd(signatureData: string, fieldName: string): void {
    // Set the form value
    this.dynamicForm.get(fieldName).setValue(signatureData);

    // Mark as touched so validation states show up
    this.dynamicForm.get(fieldName).markAsTouched();

    // If field was previously invalid and now has data, this will update validation state
    this.dynamicForm.get(fieldName).updateValueAndValidity();
  }

  onSignatureCleared(fieldName: string): void {
    // Clear the form value
    this.dynamicForm.get(fieldName).setValue('');

    // Mark as touched so validation states show up
    this.dynamicForm.get(fieldName).markAsTouched();

    // Update validation state
    this.dynamicForm.get(fieldName).updateValueAndValidity();
  }

  clearSignature(fieldName: string, event?: Event): void {
    // Prevent the event from propagating and triggering form submission
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    console.log(`Attempting to clear signature for field: ${fieldName}`);
    console.log('Available signature pads:', Array.from(this.signaturePadsByFieldName.keys()));

    const pad = this.signaturePadsByFieldName.get(fieldName);
    if (pad) {
      console.log('Signature pad found, clearing...');
      pad.clear();
      // Make sure to update the form control value
      this.dynamicForm.get(fieldName).setValue('');
      this.dynamicForm.get(fieldName).markAsTouched();
    } else {
      console.error(`Signature pad for field ${fieldName} not found`);

      // Try to find and map the signature pads again
      setTimeout(() => {
        this.mapSignaturePadsToFields();

        // Try clearing again
        const padAfterRemap = this.signaturePadsByFieldName.get(fieldName);
        if (padAfterRemap) {
          console.log('Signature pad found after remap, clearing...');
          padAfterRemap.clear();
          this.dynamicForm.get(fieldName).setValue('');
          this.dynamicForm.get(fieldName).markAsTouched();
        }
      }, 100);
    }
  }

  clearAllSignatures(): void {
    this.signaturePadsByFieldName.forEach((pad, fieldName) => {
      this.clearSignature(fieldName);
    });
  }

  // Placeholder methods for allowed types and max size
  getAllowedTypes(field: FormField): string[] {
    return field.file_upload_options?.length ? field.file_upload_options[0].allowed_file_types : [];
  }

  getMaxSize(field: FormField): number {
    return (field.file_upload_options?.[0]?.max_file_size || 10) * 1024 * 1024;
  }

  // Method to map size to Bootstrap column class
  getColumnClass(size: string): string {
    switch (size) {
      case 'full':
        return 'col-12';
      case 'half':
        return 'col-sm-12 col-md-6';
      default:
        return 'col-12'; // default to full
    }
  }
}
